/**
 * Authorization Predicates
 * 
 * Reusable predicate functions for policy evaluation.
 * These are pure functions that answer specific questions about
 * relationships, state, and context.
 * 
 * Predicates are the building blocks for policy rules.
 */

import type { ActorContext, ResourceContext, EvaluationContext } from './types';

// ============================================================================
// Relationship Predicates - "Whose thing is it?"
// ============================================================================

/**
 * Check if the actor is the owner of the resource.
 * Owner can be either the creator or explicitly assigned owner.
 */
export const isOwner = (actor: ActorContext, resource: ResourceContext): boolean =>
  resource.createdBy === actor.userId || resource.ownerId === actor.userId;

/**
 * Check if the actor is the assignee (shaping owner) of the resource.
 * Maps to resource.assigneeId (DB: shaping_owner_id).
 */
export const isAssignee = (actor: ActorContext, resource: ResourceContext): boolean =>
  resource.assigneeId === actor.userId;

/** @deprecated Use isAssignee */
export const isSteward = isAssignee;

/**
 * Check if the actor is either owner or assignee (shaping owner).
 */
export const isOwnerOrAssignee = (actor: ActorContext, resource: ResourceContext): boolean =>
  isOwner(actor, resource) || isAssignee(actor, resource);

/** @deprecated Use isOwnerOrAssignee */
export const isOwnerOrSteward = isOwnerOrAssignee;

/**
 * Roles that are allowed to edit items (in addition to owner/assignee).
 * Capability-gated at runtime; these are admin scope roles only.
 */
export const WORKSPACE_EDITOR_ROLES = [
  'manager',
  'contributor',
  'workspace-admin',
  'tenant-admin',
  'system-admin',
  'platform-admin',
] as const;

/**
 * Check if the actor can edit items via workspace role (DOCS_V4: Ball Holder, Facilitator, Workspace Admin, etc.).
 */
export const hasWorkspaceEditorRole = (actor: ActorContext): boolean =>
  hasAnyRole(actor, [...WORKSPACE_EDITOR_ROLES]);

/**
 * Check if the actor can edit this item: owner, assignee (shaping owner), or workspace editor role.
 */
export const isOwnerOrAssigneeOrWorkspaceEditor = (
  actor: ActorContext,
  resource: ResourceContext
): boolean =>
  isOwnerOrAssignee(actor, resource) || hasWorkspaceEditorRole(actor);

/** @deprecated Use isOwnerOrAssigneeOrWorkspaceEditor */
export const isOwnerOrStewardOrWorkspaceEditor = isOwnerOrAssigneeOrWorkspaceEditor;

/**
 * Check if the actor has admin privileges.
 */
export const isAdmin = (actor: ActorContext): boolean =>
  actor.isAdmin === true;

/**
 * Check if the actor has a specific role.
 */
export const hasRole = (actor: ActorContext, role: string): boolean =>
  actor.roleIds.includes(role);

/**
 * Check if the actor has any of the specified roles.
 */
export const hasAnyRole = (actor: ActorContext, roles: string[]): boolean =>
  roles.some(role => actor.roleIds.includes(role));

/**
 * Check if the actor has all of the specified roles.
 */
export const hasAllRoles = (actor: ActorContext, roles: string[]): boolean =>
  roles.every(role => actor.roleIds.includes(role));

// ============================================================================
// State Predicates - "What state is the resource in?"
// ============================================================================

/**
 * Check if the resource is locked.
 */
export const isLocked = (resource: ResourceContext): boolean =>
  resource.lockState?.isLocked === true;

/**
 * Check if a specific field is locked on the resource.
 */
export const isFieldLocked = (resource: ResourceContext, field: string): boolean =>
  resource.lockState?.lockedFields?.includes(field) ?? false;

/**
 * Check if any of the specified fields are locked.
 */
export const isAnyFieldLocked = (resource: ResourceContext, fields: string[]): boolean =>
  fields.some(field => isFieldLocked(resource, field));

/**
 * Check if the resource is in a specific lifecycle state.
 */
export const isInState = (resource: ResourceContext, state: string): boolean =>
  resource.lifecycleState === state;

/**
 * Check if the resource is in any of the specified states.
 */
export const isInAnyState = (resource: ResourceContext, states: string[]): boolean =>
  states.includes(resource.lifecycleState ?? '');

/**
 * Check if the resource has been published.
 */
export const isPublished = (resource: ResourceContext): boolean =>
  resource.isPublished === true;

/**
 * Check if the resource is in a "final" state (published, archived, etc.)
 */
export const isFinalized = (resource: ResourceContext): boolean => {
  const finalStates = ['published', 'archived', 'completed', 'finalized'];
  return (
    resource.isPublished === true ||
    finalStates.includes(resource.lifecycleState ?? '')
  );
};

/**
 * Check if the resource is in a draft/editable state.
 */
export const isDraft = (resource: ResourceContext): boolean => {
  const draftStates = ['draft', 'candidate', 'open'];
  return draftStates.includes(resource.lifecycleState ?? 'draft');
};

// ============================================================================
// Context Predicates - "When is it happening?"
// ============================================================================

/**
 * Check if the submission window is open.
 */
export const isSubmissionOpen = (context: EvaluationContext): boolean =>
  context.submissionWindowOpen === true;

// ============================================================================
// Phase predicates — UI ONLY (must NOT be used for authorization or guards)
// ============================================================================
//
// Phase is descriptive only. Authorization and guard logic must use
// invariant-based predicates (cycle_progress can_enter_*, pool_locked, etc.).
// Use canEnterPhaseFromGateState() with gate state from cycle_progress for
// any gating decisions.

/**
 * Check if the current phase matches the specified phase.
 * @deprecated For authorization/guards — must NOT gate actions on phase.
 * Use only for UI grouping or styling. For gating, use canEnterPhaseFromGateState.
 */
export const isInPhase = (context: EvaluationContext, phase: string): boolean =>
  context.phase === phase;

/**
 * Check if the current phase is in any of the specified phases.
 * @deprecated For authorization/guards — must NOT gate actions on phase.
 * Use only for UI grouping or styling. For gating, use canEnterPhaseFromGateState.
 */
export const isInAnyPhase = (context: EvaluationContext, phases: string[]): boolean =>
  phases.includes(context.phase ?? '');

/**
 * Invariant-based: whether a phase can be entered given cycle_progress gate state.
 * Use this (with gate state from cycle_progress view or readiness evaluator) for
 * authorization or guard logic instead of phase equality checks.
 */
export interface CycleGateStateForAuth {
  canEnterEngage: boolean;
  canEnterGather: boolean;
  canEnterArrange: boolean;
  canEnterPrioritize: boolean;
}

export function canEnterPhaseFromGateState(
  gateState: CycleGateStateForAuth | null | undefined,
  phase: string
): boolean {
  if (gateState == null) return false;
  switch (phase) {
    case 'DECIDE':
      return true;
    case 'ENGAGE':
      return gateState.canEnterEngage;
    case 'GATHER':
      return gateState.canEnterGather;
    case 'ARRANGE':
      return gateState.canEnterArrange;
    case 'PRIORITIZE':
      return gateState.canEnterPrioritize;
    default:
      return false;
  }
}

/**
 * Check if contributions are frozen.
 */
export const areContributionsFrozen = (context: EvaluationContext): boolean =>
  context.contributionsFrozen === true;

/**
 * Check if a feature flag is enabled.
 */
export const isFeatureEnabled = (context: EvaluationContext, flag: string): boolean =>
  context.featureFlags?.[flag] === true;

/**
 * Check if the current time is before a deadline.
 */
export const isBeforeDeadline = (context: EvaluationContext, deadline: Date): boolean => {
  const now = context.now ?? new Date();
  return now < deadline;
};

/**
 * Check if the current time is after a start time.
 */
export const isAfterStart = (context: EvaluationContext, startTime: Date): boolean => {
  const now = context.now ?? new Date();
  return now >= startTime;
};

// ============================================================================
// Compound Predicates - Common combinations
// ============================================================================

/**
 * Check if owner can edit (owner + not locked + draft state).
 */
export const canOwnerEdit = (
  actor: ActorContext,
  resource: ResourceContext
): boolean =>
  isOwner(actor, resource) && !isLocked(resource) && isDraft(resource);

/**
 * Check if edits are allowed (not locked, not published, not frozen).
 */
export const areEditsAllowed = (
  resource: ResourceContext,
  context: EvaluationContext
): boolean =>
  !isLocked(resource) &&
  !isPublished(resource) &&
  !areContributionsFrozen(context);

/**
 * Check if the actor can edit during submission window.
 */
export const canEditDuringSubmission = (
  actor: ActorContext,
  resource: ResourceContext,
  context: EvaluationContext
): boolean =>
  isOwner(actor, resource) &&
  isSubmissionOpen(context) &&
  !isLocked(resource);

/**
 * Check if assignee has override capabilities.
 */
export const canAssigneeOverride = (
  actor: ActorContext,
  resource: ResourceContext
): boolean =>
  isAssignee(actor, resource) && !isFinalized(resource);

/** @deprecated Use canAssigneeOverride */
export const canStewardOverride = canAssigneeOverride;

// ============================================================================
// Resource Type Predicates
// ============================================================================

/**
 * Check if the resource is of a specific type.
 */
export const isResourceType = (
  resource: ResourceContext,
  type: ResourceContext['type']
): boolean =>
  resource.type === type;

/**
 * Check if the resource is any of the specified types.
 */
export const isAnyResourceType = (
  resource: ResourceContext,
  types: ResourceContext['type'][]
): boolean =>
  types.includes(resource.type);

// ============================================================================
// Attribute Predicates
// ============================================================================

/**
 * Check if a resource attribute equals a specific value.
 */
export const hasAttribute = (
  resource: ResourceContext,
  key: string,
  value: unknown
): boolean =>
  resource.attributes?.[key] === value;

/**
 * Check if a resource attribute is truthy.
 */
export const hasAttributeTruthy = (
  resource: ResourceContext,
  key: string
): boolean =>
  Boolean(resource.attributes?.[key]);

/**
 * Check if a resource attribute is in a set of values.
 */
export const hasAttributeIn = (
  resource: ResourceContext,
  key: string,
  values: unknown[]
): boolean =>
  values.includes(resource.attributes?.[key]);
